// Returns table — All / Long / Short columns mirroring TradingView's "Returns" panel.
// Rows: Initial capital, Open P&L (we don't track; shown as "—"), Net P&L, Gross profit,
// Gross loss, Profit factor, Commission paid, Expected payoff.
//
// "All" column derives from merged when present, otherwise sums the two scans.

import type { BacktestResponseDto, ScanResultDto, MergeResultDto, NettingResultDto } from "../../api/types";

interface Props {
  result: BacktestResponseDto;
}

interface Column {
  label: string;
  netPnl: number | null;
  grossProfit: number | null;
  grossLoss: number | null;
  profitFactor: number | null;
  commissionPaid: number | null;
  expectedPayoff: number | null;
  maxRunup: number | null;
  largestWin: number | null;
  largestLoss: number | null;
  maxConsecWins: number | null;
  maxConsecLosses: number | null;
}

// Netting and scan share these field names, so one projector serves both.
function columnFromScan(label: string, s: ScanResultDto | NettingResultDto | null): Column {
  if (!s) return blankColumn(label);
  return {
    label,
    netPnl: s.totalReturn,
    grossProfit: s.grossProfit,
    grossLoss: s.grossLoss,
    profitFactor: s.profitFactor,
    commissionPaid: s.commissionPaid,
    expectedPayoff: s.expectedPayoff,
    maxRunup: s.maxRunup,
    largestWin: s.largestWin,
    largestLoss: s.largestLoss,
    maxConsecWins: s.maxConsecutiveWins,
    maxConsecLosses: s.maxConsecutiveLosses,
  };
}

function columnFromMerge(label: string, m: MergeResultDto): Column {
  return {
    label,
    netPnl: m.totalReturn,
    grossProfit: m.grossProfit,
    grossLoss: m.grossLoss,
    profitFactor: m.profitFactor,
    commissionPaid: m.commissionPaid,
    expectedPayoff: m.expectedPayoff,
    maxRunup: m.maxRunup,
    largestWin: m.largestWin,
    largestLoss: m.largestLoss,
    maxConsecWins: m.maxConsecutiveWins,
    maxConsecLosses: m.maxConsecutiveLosses,
  };
}

function blankColumn(label: string): Column {
  return {
    label,
    netPnl: null, grossProfit: null, grossLoss: null, profitFactor: null,
    commissionPaid: null, expectedPayoff: null, maxRunup: null,
    largestWin: null, largestLoss: null, maxConsecWins: null, maxConsecLosses: null,
  };
}

export function ReturnsTable({ result }: Props) {
  // Phase 12: the netting account is the primary "Strategy" column; the long/short scans
  // remain as single-direction diagnostic columns alongside it.
  const all = result.strategy
    ? columnFromScan("Strategy", result.strategy)
    : result.merged
      ? columnFromMerge("All", result.merged)
      : columnFromScan("All", result.long ?? result.short);
  const longCol = columnFromScan("Long (diag)", result.long);
  const shortCol = columnFromScan("Short (diag)", result.short);
  const cols = [all, longCol, shortCol];

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Returns</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Metric</th>
            {cols.map((c) => (
              <th key={c.label} style={{ ...thStyle, textAlign: "right" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <Row label="Initial capital" values={["100.00%", "100.00%", "100.00%"]} />
          <Row label="Open P&L" values={cols.map(() => "—")} muted />
          <Row label="Net P&L"        values={cols.map((c) => fmtPct(c.netPnl))} signColor />
          <Row label="Gross profit"   values={cols.map((c) => fmtPct(c.grossProfit))} positive />
          <Row label="Gross loss"     values={cols.map((c) => fmtPct(c.grossLoss == null ? null : -c.grossLoss))} negative />
          <Row label="Profit factor"  values={cols.map((c) => fmt(c.profitFactor))} pfColor />
          <Row label="Commission paid" values={cols.map((c) => fmtPct(c.commissionPaid == null ? null : -c.commissionPaid))} negative />
          <Row label="Expected payoff" values={cols.map((c) => fmtPct(c.expectedPayoff))} signColor />
          <Row label="Max run-up"      values={cols.map((c) => fmtPct(c.maxRunup))} positive />
          <Row label="Largest win"     values={cols.map((c) => fmtPct(c.largestWin))} positive />
          <Row label="Largest loss"    values={cols.map((c) => fmtPct(c.largestLoss))} negative />
          <Row label="Max consec wins"   values={cols.map((c) => c.maxConsecWins == null ? "—" : String(c.maxConsecWins))} />
          <Row label="Max consec losses" values={cols.map((c) => c.maxConsecLosses == null ? "—" : String(c.maxConsecLosses))} />
        </tbody>
      </table>
    </div>
  );
}

function Row({
  label, values, signColor: useSignColor, positive, negative, pfColor, muted,
}: { label: string; values: string[]; signColor?: boolean; positive?: boolean; negative?: boolean; pfColor?: boolean; muted?: boolean }) {
  return (
    <tr style={{ borderTop: "1px solid #21262d" }}>
      <td style={{ padding: "6px 8px", color: "#8b949e", fontSize: 12 }}>{label}</td>
      {values.map((v, i) => {
        let color = "#c9d1d9";
        if (muted) color = "#8b949e";
        else if (useSignColor) color = v.startsWith("+") ? "#3fb950" : v.startsWith("-") ? "#f85149" : "#c9d1d9";
        else if (positive) color = "#3fb950";
        else if (negative) color = "#f85149";
        else if (pfColor) {
          const num = parseFloat(v);
          color = !Number.isNaN(num) && num >= 1 ? "#3fb950" : "#f85149";
        }
        return (
          <td key={i} style={{ padding: "6px 8px", textAlign: "right", color, fontFamily: "monospace", fontSize: 12 }}>
            {v}
          </td>
        );
      })}
    </tr>
  );
}

const thStyle: React.CSSProperties = {
  padding: "6px 8px",
  textAlign: "left",
  fontSize: 11,
  color: "#8b949e",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  background: "#161b22",
};

function fmt(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function fmtPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}
